import { useState, useMemo } from 'react';
import { Content, Row, Col } from 'components';
import { Table, Input, Button, Space, Spin } from 'antd';
import { useGetProducts, useGetProduct } from 'hooks/ProductHooks';
import MessageAlert from 'utils';
import { useUserStack } from 'store';
import { SearchOutlined } from '@ant-design/icons';

/**----------------------------------------
 * [Component] 총관리자: 상품등록 - 상품 불러오기
 * ----------------------------------------
 */
const ProductLoadModal = ({ onDefaultData, onIsOpenLoadModal }) => {
  const userInfo = useUserStack();
  // 모바일 전체 메뉴 모달
  const { data: getProducts } = useGetProducts({
    shop_information_id: userInfo?.shop_information_id,
  });

  /* ===== STATE ===== */
  const [isLoading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null); // 선택된 상품 ID 저장
  const [searchText, setSearchText] = useState(''); // 검색 텍스트 상태

  const newProducts = useMemo(() => {
    setLoading(true);
    try {
      if (getProducts && getProducts?.status === 200) {
        const { data } = getProducts;
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return data;
      }
    } catch (e) {
      MessageAlert.error('상품 정보를 불러오는데 실패 했습니다.');
      return [];
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  }, [getProducts]);

  const filteredProducts = useMemo(() => {
    return newProducts?.length > 0
      ? newProducts?.filter(
          (product) =>
            product?.product_code
              .toLowerCase()
              ?.includes(searchText.toLowerCase()) ||
            product?.product_name
              .toLowerCase()
              ?.includes(searchText.toLowerCase())
        )
      : [];
  }, [newProducts, searchText]);

  const columns = [
    {
      title: '상품번호',
      key: 'product_id',
      dataIndex: 'product_id',
      width: '20%',
      sorter: (a, b) => a.product_id - b.product_id,
    },
    {
      title: '상품코드',
      key: 'product_code',
      dataIndex: 'product_code',
      width: '30%',
      filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={selectedKeys[0]}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => confirm()}
            placeholder={`상품코드 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                setSearchText(''); // 초기화 시 검색 텍스트 비우기
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        return record.product_code.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: '상품명',
      key: 'product_name',
      dataIndex: 'product_name',
      width: '40%',
      filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={selectedKeys[0]}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => confirm()}
            placeholder={`상품명 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                setSearchText(''); // 초기화 시 검색 텍스트 비우기
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        return record.product_name.toLowerCase().includes(value.toLowerCase());
      },
    },
  ];
  return (
    <Spin spinning={isLoading}>
      <Content maxWidth={'100%'} padding={0}>
        <Row>
          <Col x={24}>
            <Content
              maxWidth={'100%'}
              minHeight={'100%'}
              padding={0}
              style={{ marginBottom: 20 }}
            >
              <Table
                rowKey="product_id" // 각 행의 고유 키 설정
                dataSource={filteredProducts} // 필터링된 데이터 사용
                minHeight={'auto'}
                // pagination={true}
                columns={columns}
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys: [selectedProduct?.product_id],
                  onChange: (_, selectedRows) => {
                    setSelectedProduct(selectedRows[0]);
                  },
                }}
                style={{ maxHeight: '700px', overflowY: 'auto' }} // 고정된 높이 및 스크롤 처리
              />
              <Button
                size="middle"
                type="primary"
                onClick={() => {
                  const defaultValue = {
                    ...selectedProduct,
                    product_id: null,
                    product_code: null,
                    apply_feedback: null,
                    interruption_reason: null,
                    delete_reason: null,
                    application_status: 'APPLY',
                    product_status: 'WAIT',
                  };
                  onDefaultData(defaultValue);
                  setTimeout(() => {
                    onIsOpenLoadModal(false);
                  }, 200);
                }}
              >
                선택된 상품 불러오기
              </Button>
            </Content>
          </Col>
        </Row>
      </Content>
    </Spin>
  );
};

export default ProductLoadModal;
